// src/pages/Public/Payment/PublicPaymentPage.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from "../../../../utils/axiosInstance";
import backendURL from '../../../../config';
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField, 
  RadioGroup, FormControlLabel, Radio, Chip, Alert, CircularProgress,
  FormControl, FormLabel, Checkbox, Divider, Container, Paper,
  InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  Step, Stepper, StepLabel, Avatar, Stack, LinearProgress,
  Skeleton, CardHeader, IconButton, Tooltip, Fade, Grow,
  useTheme, alpha, styled, Select, MenuItem, InputLabel
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

// Enhanced Icons
import PayPalIcon from '@mui/icons-material/Payment';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import RepeatIcon from '@mui/icons-material/Repeat';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import useEmbedMode from './useEmbedMode';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const paypalInitialOptions = {
  clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID,
  currency: "USD",
  intent: "capture",
  vault: true
};

// Enhanced Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  }
}));

const ProfessionalCard = styled(Card)(({ theme, selected }) => ({
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: selected 
    ? `2px solid ${theme.palette.primary.main}` 
    : `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  borderRadius: theme.spacing(1.5),
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
    borderColor: theme.palette.primary.main,
  },
  ...(selected && {
    boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}, ${theme.shadows[4]}`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: 0,
      height: 0,
      borderLeft: '20px solid transparent',
      borderTop: `20px solid ${theme.palette.primary.main}`,
    },
    '&::after': {
      content: '"✓"',
      position: 'absolute',
      top: '4px',
      right: '4px',
      color: 'white',
      fontSize: '12px',
      fontWeight: 'bold',
    }
  })
}));

const SecurityBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5),
  backgroundColor: alpha(theme.palette.success.main, 0.1),
  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
  borderRadius: theme.spacing(4),
  color: theme.palette.success.dark,
  fontSize: '0.75rem',
  fontWeight: 500,
}));

const AmountButton = styled(Button)(({ theme, selected }) => ({
  height: '56px',
  borderRadius: theme.spacing(1),
  fontSize: '1.1rem',
  fontWeight: selected ? 600 : 500,
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  ...(selected && {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    color: 'white',
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[4],
  }),
  '&:hover': {
    transform: selected ? 'scale(1.02)' : 'scale(1.01)',
  }
}));

const ProcessingSteps = ({ activeStep }) => {
  const steps = ['Payment Details', 'Processing', 'Confirmation'];
  
  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

const PublicPaymentPage = () => {
  const { payToken } = useParams();
  const [paymentData, setPaymentData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const theme = useTheme();
  const embed = useEmbedMode();

  useEffect(() => {
    if (payToken) {
      fetchPaymentInfo();
    }
  }, [payToken]);

  const fetchPaymentInfo = async () => {
    try {
      const response = await axiosInstance.get(`/public/advanced-payment-info/${payToken}`);
      setPaymentData(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load payment information');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, ...embed.containerSx }}>
        <GradientPaper sx={{ p: 4, ...embed.paperSx }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 2 }} />
            <Skeleton variant="text" width="60%" height={40} />
            <Box sx={{ width: '100%', display: 'flex', gap: 2 }}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} variant="rectangular" height={56} sx={{ flex: 1, borderRadius: 1 }} />
              ))}
            </Box>
            <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 2 }} />
          </Box>
        </GradientPaper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, ...embed.containerSx }}>
        <GradientPaper sx={{ p: 4, ...embed.paperSx }}>
          <Box sx={{ textAlign: 'center' }}>
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>Payment Link Error</Typography>
            <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
          </Box>
        </GradientPaper>
      </Container>
    );
  }

  const dynamicPayPalOptions = {
    ...paypalInitialOptions,
    vault: paymentData.payment_frequencies.recurring === true
  };

  return (
    <Elements stripe={stripePromise}>
      <PayPalScriptProvider options={dynamicPayPalOptions}>
        <PaymentForm paymentData={paymentData} embed={embed} />
      </PayPalScriptProvider>
    </Elements>
  );
};

const PaymentForm = ({ paymentData, embed }) => {
  const stripe = useStripe();
  const elements = useElements();
  const theme = useTheme();

  const [formState, setFormState] = useState({
    selectedAmount: '',
    customAmount: '',
    selectedPurpose: '',
    customerInfo: {
      name: '',
      email: ''
    },
    includeProcessingFee: false,
    selectedPaymentMethod: 'stripe',
    selectedRecurringType: 'onetime',
    selectedRecurringInterval: 'monthly',
    isProcessing: false,
    error: '',
    showThankYou: false,
    paymentResult: null,
    currentStep: 0
  });

  useEffect(() => {
    // Auto-select the first available payment method
    const methods = paymentData.payment_methods || {};
    const availableMethods = ['stripe', 'paypal', 'coinbase'].filter(m => methods[m]);
    const firstMethod = availableMethods.length > 0 ? availableMethods[0] : 'stripe';

    if (paymentData.payment_frequencies.recurring) {
      setFormState(prev => ({
        ...prev,
        selectedRecurringInterval: paymentData.recurring_interval || 'monthly',
        selectedPurpose: paymentData.purposes?.[0] || '',
        selectedPaymentMethod: firstMethod
      }));
    } else {
      setFormState(prev => ({
        ...prev,
        selectedPurpose: paymentData.purposes?.[0] || '',
        selectedPaymentMethod: firstMethod
      }));
    }
  }, [paymentData]);

  const handleAmountSelection = (amount) => {
    setFormState(prev => ({
      ...prev,
      selectedAmount: amount,
      customAmount: amount
    }));
  };

  const calculateTotal = () => {
    // Get the final amount - use customAmount as the single source of truth
    const baseAmount = parseFloat(formState.customAmount) || 0;
    let processingFee = 0;

    // Always calculate the potential fee so checkbox label can show it
    const potentialFee = (baseAmount * paymentData.processing_fee_percentage / 100) + paymentData.fixed_fee;

    if (formState.includeProcessingFee || paymentData.processing_fee_handling === 'added') {
      processingFee = potentialFee;
    }

    return {
      baseAmount: baseAmount.toFixed(2),
      processingFee: processingFee.toFixed(2),
      potentialFee: potentialFee.toFixed(2),
      totalAmount: (baseAmount + processingFee).toFixed(2)
    };
  };

  const validateForm = () => {
    // Validate using customAmount as the single source of truth
    const amount = parseFloat(formState.customAmount) || 0;
    console.log('Validating form with amount:', amount);
    
    if (amount <= 0) {
      setFormState(prev => ({ ...prev, error: 'Please select or enter a valid amount' }));
      return false;
    }

    if (formState.selectedRecurringType === 'recurring' && !formState.selectedRecurringInterval) {
      setFormState(prev => ({ ...prev, error: 'Please select a recurring interval' }));
      return false;
    }

    if (paymentData.collect_customer_info && (!formState.customerInfo.name || !formState.customerInfo.email)) {
      setFormState(prev => ({ ...prev, error: 'Please provide your name and email' }));
      return false;
    }

    console.log('Form validation passed');
    return true;
  };

  const createStripePaymentIntent = async () => {
    // Use customAmount as the final amount to send to API
    const amount = parseFloat(formState.customAmount);
    
    console.log('Creating Stripe payment intent with:', {
      payment_token: paymentData.token,
      amount: amount,
      payment_method: 'stripe',
      recurring_type: formState.selectedRecurringType,
      recurring_interval: formState.selectedRecurringInterval,
      selected_purpose: formState.selectedPurpose,
      customer_info: formState.customerInfo
    });
    
    const response = await axiosInstance.post(`/process-advanced-payment`, {
      payment_token: paymentData.token,
      amount: amount,
      include_processing_fee: formState.includeProcessingFee,
      customer_info: formState.customerInfo,
      payment_method: 'stripe',
      recurring_type: formState.selectedRecurringType,
      recurring_interval: formState.selectedRecurringInterval,
      selected_purpose: formState.selectedPurpose,
    });

    console.log('Stripe payment intent response:', response.data);
    return response.data;
  };

  const handleStripePayment = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements || !validateForm()) return;

    setFormState(prev => ({ ...prev, isProcessing: true, error: '', currentStep: 1 }));

    try {
      const response = await createStripePaymentIntent();
      const { clientSecret, subscriptionId, customerId } = response;
      
      if (!clientSecret) {
        throw new Error('No client secret received from server');
      }

      const cardElement = elements.getElement(CardElement);

      let result;
      if (formState.selectedRecurringType === 'recurring') {
        // For subscriptions, use confirmCardPayment with the subscription's invoice payment intent
        result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: formState.customerInfo.name,
              email: formState.customerInfo.email,
            },
          }
        });
      } else {
        // For one-time payments
        result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: formState.customerInfo.name,
              email: formState.customerInfo.email,
            },
          }
        });
      }

      const { error, paymentIntent } = result;

      if (error) {
        setFormState(prev => ({ ...prev, error: error.message, isProcessing: false, currentStep: 0 }));
      } else {
        // Verify payment with backend
        await axiosInstance.post(`/verify-advanced-payment`, {
          payment_intent_id: paymentIntent.id,
          subscription_id: subscriptionId || null
        });
        
        setFormState(prev => ({ 
          ...prev, 
          showThankYou: true, 
          isProcessing: false,
          currentStep: 2,
          paymentResult: { 
            method: 'stripe', 
            recurring: formState.selectedRecurringType === 'recurring',
            subscriptionId: subscriptionId || null
          }
        }));
      }

    } catch (err) {
      console.error('Stripe payment error:', err);
      setFormState(prev => ({
        ...prev,
        error: err.response?.data?.message || err.message || 'Payment failed',
        isProcessing: false,
        currentStep: 0
      }));
    }
  };

  const createPayPalOrder = async () => {
    if (!validateForm()) {
        throw new Error('Form validation failed');
    }

    try {
        console.log('Creating PayPal order...');
        console.log('Form state:', {
        customAmount: formState.customAmount,
        selectedRecurringType: formState.selectedRecurringType,
        selectedRecurringInterval: formState.selectedRecurringInterval,
        customerInfo: formState.customerInfo
        });
        
        const requestPayload = {
        payment_token: paymentData.token,
        amount: parseFloat(formState.customAmount),
        include_processing_fee: formState.includeProcessingFee,
        customer_info: formState.customerInfo,
        payment_method: 'paypal',
        recurring_type: formState.selectedRecurringType,
        recurring_interval: formState.selectedRecurringInterval,
        selected_purpose: formState.selectedPurpose,
        };
        
        console.log('Sending request payload:', requestPayload);
        
        const response = await axiosInstance.post(`/process-advanced-payment`, requestPayload);
        
        console.log('Full API response:', response);
        console.log('Response data:', response.data);
        console.log('Response status:', response.status);
        
        if (response.data.success) {
        if (response.data.paypalOrderId) {
            console.log('PayPal Order ID received:', response.data.paypalOrderId);
            return response.data.paypalOrderId;
        } else {
            console.error('Response data missing paypalOrderId:', response.data);
            throw new Error('No PayPal order ID received from server');
        }
        } else {
        console.error('API returned success: false:', response.data);
        throw new Error(response.data.message || 'API returned success: false');
        }
    } catch (error) {
        console.error('PayPal order creation error:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        
        if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
        } else if (error.message) {
        throw new Error(error.message);
        } else {
        throw new Error('Failed to create PayPal order - unknown error');
        }
    }
    };

  const onPayPalApprove = async (data, actions) => {
    try {
      setFormState(prev => ({ ...prev, isProcessing: true, currentStep: 1 }));
      
      const response = await axiosInstance.post(`/verify-paypal-payment`, {
        orderID: data.orderID,
        subscriptionID: data.subscriptionID,
        payment_token: paymentData.token
      });

      if (response.data.success) {
        setFormState(prev => ({ 
          ...prev, 
          showThankYou: true, 
          isProcessing: false,
          currentStep: 2,
          paymentResult: { 
            method: 'paypal', 
            recurring: formState.selectedRecurringType === 'recurring',
            subscriptionID: data.subscriptionID 
          }
        }));
      } else {
        throw new Error(response.data.message || 'PayPal payment verification failed');
      }
    } catch (error) {
      console.error('PayPal approval error:', error);
      setFormState(prev => ({
        ...prev,
        error: error.response?.data?.message || error.message || 'PayPal payment failed',
        isProcessing: false,
        currentStep: 0
      }));
    }
  };

  const onPayPalError = (err) => {
    console.error('PayPal error:', err);
    setFormState(prev => ({
      ...prev,
      error: `PayPal payment failed: ${err.message || 'An error occurred'}`,
      isProcessing: false,
      currentStep: 0
    }));
  };

  const amounts = calculateTotal();
  const customerLabel = paymentData.payment_type === 'donation' ? 'Donor' : 'Customer';
  const isRecurring = formState.selectedRecurringType === 'recurring';

  const getDynamicPayPalOptions = () => {
    const isRecurring = formState.selectedRecurringType === 'recurring';
    
    const options = {
        clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID,
        currency: "USD",
        intent: isRecurring ? "subscription" : "capture",
        vault: isRecurring,
        components: "buttons,funding-eligibility"
    };
    
    // For recurring payments, disable funding sources that don't support subscriptions
    if (isRecurring) {
        options['disable-funding'] = "credit,card,bancontact,blik,eps,giropay,ideal,mercadopago,mybank,p24,sepa,sofort,venmo";
    }
    
    console.log('PayPal options:', options);
    return options;
    };

  if (formState.showThankYou) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, ...embed.containerSx }}>
        <Grow in timeout={800}>
          <GradientPaper sx={{ p: 4, textAlign: 'center', ...embed.paperSx }}>
            <ProcessingSteps activeStep={2} />
            
            <Box sx={{ mb: 4 }}>
              <Avatar sx={{ 
                width: 80, 
                height: 80, 
                mx: 'auto', 
                mb: 2,
                background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})` 
              }}>
                <CheckCircleIcon sx={{ fontSize: 40 }} />
              </Avatar>
              
              <Typography variant="h3" sx={{ 
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                backgroundClip: 'text',
                textFillColor: 'transparent',
                mb: 2
              }}>
                Payment Successful!
              </Typography>
              
              <Typography variant="h6" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                {paymentData.thank_you_message}
              </Typography>
            </Box>

            <Stack spacing={2} sx={{ maxWidth: 500, mx: 'auto', mb: 4 }}>
              <Card sx={{ p: 2, background: alpha(theme.palette.success.main, 0.05) }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <AttachMoneyIcon />
                    </Avatar>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h6" color="success.main">
                      ${amounts.totalAmount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isRecurring ? `${formState.selectedRecurringInterval} recurring payment` : 'One-time payment'}
                    </Typography>
                  </Grid>
                </Grid>
              </Card>

              <Card sx={{ p: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <Avatar sx={{ bgcolor: formState.paymentResult?.method === 'coinbase' ? '#f7931a' : 'primary.main' }}>
                      {formState.paymentResult?.method === 'coinbase' ? <CurrencyBitcoinIcon /> : <ReceiptIcon />}
                    </Avatar>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="subtitle1">
                      Payment Method: {
                        formState.paymentResult?.method === 'paypal' ? 'PayPal'
                        : formState.paymentResult?.method === 'coinbase' ? `Crypto (${formState.paymentResult?.crypto_token || 'USDC'})`
                        : 'Credit Card'
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formState.paymentResult?.method === 'coinbase'
                        ? 'Payment sent — awaiting blockchain confirmation'
                        : 'Processed securely'
                      }
                    </Typography>
                  </Grid>
                </Grid>
              </Card>
            </Stack>

            {formState.paymentResult?.recurring && (
              <Alert
                severity="info"
                icon={<RepeatIcon />}
                sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}
              >
                <Typography variant="body2">
                  Your {formState.selectedRecurringInterval} recurring payment has been set up successfully.
                  {formState.paymentResult.method === 'stripe' && formState.paymentResult.subscriptionId && (
                    <span> Subscription ID: {formState.paymentResult.subscriptionId}</span>
                  )}
                  {formState.paymentResult.method === 'paypal' && formState.paymentResult.subscriptionID && (
                    <span> Subscription ID: {formState.paymentResult.subscriptionID}</span>
                  )}
                </Typography>
              </Alert>
            )}

            {formState.paymentResult?.method === 'coinbase' && (
              <Alert
                severity="warning"
                icon={<CurrencyBitcoinIcon />}
                sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}
              >
                <Typography variant="body2">
                  <strong>Crypto Payment Note:</strong> Your payment has been initiated via Coinbase Commerce.
                  Blockchain confirmations typically take 1-10 minutes depending on the network.
                  You will receive a confirmation email once the payment is fully confirmed.
                </Typography>
              </Alert>
            )}

            {paymentData.redirect_url && (
              <Button
                variant="contained"
                size="large"
                sx={{ mt: 3, px: 4, py: 1.5 }}
                onClick={() => embed.embedRedirect(paymentData.redirect_url)}
              >
                Continue to Next Step
              </Button>
            )}

            <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <SecurityBadge>
                <SecurityIcon sx={{ fontSize: 16 }} />
                Your payment is secure and encrypted
              </SecurityBadge>
            </Box>
          </GradientPaper>
        </Grow>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4, ...embed.containerSx }}>
      <GradientPaper sx={{ overflow: 'visible', ...embed.paperSx }}>
        {formState.isProcessing && (
          <LinearProgress sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0,
            height: 3,
            borderRadius: '4px 4px 0 0'
          }} />
        )}

        <Box sx={{ p: { xs: 3, md: 4 } }}>
          {/* Processing Steps */}
          <ProcessingSteps activeStep={formState.currentStep} />

          {/* Header Section */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h3" component="h1" sx={{ 
              fontWeight: 800,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              textFillColor: 'transparent',
              mb: 1
            }}>
              {paymentData.title}
            </Typography>
            
            <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
              {paymentData.display_name}
            </Typography>
            
            {paymentData.description && (
              <Typography variant="body1" sx={{ mt: 2, maxWidth: 600, mx: 'auto', color: 'text.secondary' }}>
                {paymentData.description}
              </Typography>
            )}
            
            {isRecurring && (
              <Fade in timeout={600}>
                <Box sx={{ mt: 3 }}>
                  <Chip
                    icon={<RepeatIcon />}
                    label={`${formState.selectedRecurringInterval.charAt(0).toUpperCase() + formState.selectedRecurringInterval.slice(1)} Recurring Payment`}
                    color="primary"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                      height: 'auto'
                    }}
                  />
                </Box>
              </Fade>
            )}
          </Box>

          <form onSubmit={handleStripePayment}>

            {/* Purpose Selection */}
            {paymentData.purposes && paymentData.purposes.length > 0 && (
              <ProfessionalCard sx={{ mb: 3 }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      <InfoIcon />
                    </Avatar>
                  }
                  title={
                    <Typography variant="h6" fontWeight={600}>
                      Select Purpose
                    </Typography>
                  }
                  subheader="Choose what this payment is for"
                />
                <CardContent>
                  <FormControl fullWidth>
                    <InputLabel>Purpose/Category</InputLabel>
                    <Select
                      value={formState.selectedPurpose || (paymentData.purposes[0] || '')}
                      onChange={(e) => setFormState(prev => ({ 
                        ...prev, 
                        selectedPurpose: e.target.value 
                      }))}
                      label="Purpose/Category"
                      sx={{
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        }
                      }}
                    >
                      {paymentData.purposes.map((purpose, index) => (
                        <MenuItem key={index} value={purpose}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                            <Typography variant="body1" fontWeight={500}>
                              {purpose}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Show selected purpose as a chip for visual feedback */}
                  {formState.selectedPurpose && (
                    <Box sx={{ mt: 2 }}>
                      <Chip
                        label={`Selected: ${formState.selectedPurpose}`}
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </Box>
                  )}
                </CardContent>
              </ProfessionalCard>
            )}
            
            {/* Amount Selection */}
            <ProfessionalCard sx={{ mb: 3 }}>
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <AttachMoneyIcon />
                  </Avatar>
                }
                title={
                  <Typography variant="h6" fontWeight={600}>
                    Select Amount {isRecurring && `(${formState.selectedRecurringInterval})`}
                  </Typography>
                }
                subheader="Choose your payment amount"
              />
              <CardContent>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {paymentData.suggested_amounts.map((amount, index) => (
                    <Grid item xs={6} sm={3} key={index}>
                      <AmountButton
                        variant={formState.selectedAmount === amount ? "contained" : "outlined"}
                        fullWidth
                        selected={formState.selectedAmount === amount}
                        onClick={() => handleAmountSelection(amount)}
                      >
                        ${amount}
                        {isRecurring && (
                          <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
                            /{formState.selectedRecurringInterval}
                          </Typography>
                        )}
                      </AmountButton>
                    </Grid>
                  ))}
                </Grid>

                {paymentData.custom_amount_enabled && (
                  <TextField
                    fullWidth
                    label={`Amount ${isRecurring ? `(per ${formState.selectedRecurringInterval})` : ''}`}
                    type="number"
                    value={formState.customAmount}
                    onChange={(e) => setFormState(prev => ({ ...prev, customAmount: e.target.value }))}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    inputProps={{ step: "0.01", min: "0.50" }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                    helperText={
                      formState.customAmount && parseFloat(formState.customAmount) > 0 
                        ? `Final amount: ${parseFloat(formState.customAmount).toFixed(2)}${isRecurring ? ` ${formState.selectedRecurringInterval}` : ''}` 
                        : 'Enter your desired amount or select from suggested amounts above'
                    }
                  />
                )}
              </CardContent>
            </ProfessionalCard>

            {/* Payment Frequency */}
            <ProfessionalCard sx={{ mb: 3 }}>
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>
                    <TrendingUpIcon />
                  </Avatar>
                }
                title={
                  <Typography variant="h6" fontWeight={600}>
                    Payment Frequency
                  </Typography>
                }
                subheader="Choose how often you want to pay"
              />
              <CardContent>
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    value={formState.selectedRecurringType}
                    onChange={(e) => setFormState(prev => ({ ...prev, selectedRecurringType: e.target.value }))}
                  >
                    <FormControlLabel 
                      value="onetime" 
                      control={<Radio />} 
                      label={
                        <Box>
                          <Typography variant="subtitle1" fontWeight={500}>One-Time Payment</Typography>
                          <Typography variant="body2" color="text.secondary">Pay once and you're done</Typography>
                        </Box>
                      }
                      sx={{ py: 1 }}
                    />
                    {paymentData.payment_frequencies.recurring && (
                      <FormControlLabel 
                        value="recurring" 
                        control={<Radio />} 
                        label={
                          <Box>
                            <Typography variant="subtitle1" fontWeight={500}>
                              Recurring Payment
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Set up automatic recurring payments
                            </Typography>
                          </Box>
                        }
                        sx={{ py: 1 }}
                      />
                    )}
                  </RadioGroup>
                </FormControl>

                {/* Recurring Interval Selection */}
                {formState.selectedRecurringType === 'recurring' && (
                  <Fade in timeout={300}>
                    <Box sx={{ mt: 3, pl: 4 }}>
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                        Select Payment Interval
                      </Typography>
                      <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                          value={formState.selectedRecurringInterval}
                          onChange={(e) => setFormState(prev => ({ ...prev, selectedRecurringInterval: e.target.value }))}
                        >
                          <Grid container spacing={2}>
                            {[
                              { value: 'daily', label: 'Daily', description: 'Charged every day', icon: '📅' },
                              { value: 'weekly', label: 'Weekly', description: 'Charged every week', icon: '📊' },
                              { value: 'monthly', label: 'Monthly', description: 'Charged every month', icon: '📈' },
                              { value: 'yearly', label: 'Yearly', description: 'Charged every year', icon: '🎯' }
                            ].map((interval) => (
                              <Grid item xs={12} sm={6} key={interval.value}>
                                <ProfessionalCard 
                                  selected={formState.selectedRecurringInterval === interval.value}
                                  onClick={() => setFormState(prev => ({ ...prev, selectedRecurringInterval: interval.value }))}
                                  sx={{ 
                                    p: 0, 
                                    cursor: 'pointer',
                                    minHeight: 'auto'
                                  }}
                                >
                                  <FormControlLabel 
                                    value={interval.value} 
                                    control={<Radio sx={{ display: 'none' }} />}
                                    label={
                                      <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 2, 
                                        p: 2, 
                                        width: '100%'
                                      }}>
                                        <Typography sx={{ fontSize: '1.5rem' }}>
                                          {interval.icon}
                                        </Typography>
                                        <Box sx={{ flexGrow: 1 }}>
                                          <Typography variant="subtitle1" fontWeight={600}>
                                            {interval.label}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            {interval.description}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    }
                                    sx={{ 
                                      m: 0, 
                                      width: '100%',
                                      '& .MuiFormControlLabel-label': {
                                        width: '100%'
                                      }
                                    }}
                                  />
                                </ProfessionalCard>
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                      </FormControl>
                      
                      {/* Interval Preview */}
                      <Box sx={{ 
                        mt: 3, 
                        p: 2, 
                        borderRadius: 2, 
                        background: alpha(theme.palette.primary.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                      }}>
                        <Typography variant="body2" color="primary.main" fontWeight={500}>
                          💡 You selected: <strong>{formState.selectedRecurringInterval.charAt(0).toUpperCase() + formState.selectedRecurringInterval.slice(1)}</strong> payments
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {formState.selectedRecurringInterval === 'daily' && 'Payments will be processed every day at the same time.'}
                          {formState.selectedRecurringInterval === 'weekly' && 'Payments will be processed every week on the same day.'}
                          {formState.selectedRecurringInterval === 'monthly' && 'Payments will be processed every month on the same date.'}
                          {formState.selectedRecurringInterval === 'yearly' && 'Payments will be processed every year on the same date.'}
                        </Typography>
                      </Box>
                    </Box>
                  </Fade>
                )}
              </CardContent>
            </ProfessionalCard>

            {/* Customer Information */}
            {paymentData.collect_customer_info && (
              <ProfessionalCard sx={{ mb: 3 }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'info.main' }}>
                      <PersonIcon />
                    </Avatar>
                  }
                  title={
                    <Typography variant="h6" fontWeight={600}>
                      {customerLabel} Information
                    </Typography>
                  }
                  subheader="We need some basic information to process your payment"
                />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="Full Name"
                        value={formState.customerInfo.name}
                        onChange={(e) => setFormState(prev => ({
                          ...prev,
                          customerInfo: { ...prev.customerInfo, name: e.target.value }
                        }))}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        type="email"
                        label="Email Address"
                        value={formState.customerInfo.email}
                        onChange={(e) => setFormState(prev => ({
                          ...prev,
                          customerInfo: { ...prev.customerInfo, email: e.target.value }
                        }))}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </ProfessionalCard>
            )}

            {/* Processing Fee Option */}
            {paymentData.processing_fee_handling === 'user_choice' && (
              <ProfessionalCard sx={{ mb: 3 }}>
                <CardContent>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formState.includeProcessingFee}
                        onChange={(e) => setFormState(prev => ({ 
                          ...prev, 
                          includeProcessingFee: e.target.checked 
                        }))}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="subtitle1" fontWeight={500}>
                          Cover the processing fee (+${amounts.potentialFee})
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Help us keep more of your {paymentData.payment_type === 'donation' ? 'donation' : 'payment'}
                        </Typography>
                      </Box>
                    }
                  />
                </CardContent>
              </ProfessionalCard>
            )}

            {/* Payment Method Selection */}
            <ProfessionalCard sx={{ mb: 3 }}>
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <CreditCardIcon />
                  </Avatar>
                }
                title={
                  <Typography variant="h6" fontWeight={600}>
                    Payment Method
                  </Typography>
                }
                subheader="Choose your preferred payment method"
              />
              <CardContent>
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    value={formState.selectedPaymentMethod}
                    onChange={(e) => setFormState(prev => ({ 
                      ...prev, 
                      selectedPaymentMethod: e.target.value 
                    }))}
                  >
                    {paymentData.payment_methods.stripe && (
                      <FormControlLabel
                        value="stripe"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                            <CreditCardIcon color="primary" />
                            <Box>
                              <Typography variant="subtitle1" fontWeight={500}>
                                Credit/Debit Card
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Visa, MasterCard, American Express
                              </Typography>
                            </Box>
                            {paymentData.payment_frequencies.recurring && (
                              <Chip size="small" label="Supports Recurring" color="primary" variant="outlined" />
                            )}
                          </Box>
                        }
                        sx={{ py: 1 }}
                      />
                    )}
                    
                    {paymentData.payment_methods.paypal && (
                      <FormControlLabel
                        value="paypal"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                            <PayPalIcon color="primary" />
                            <Box>
                              <Typography variant="subtitle1" fontWeight={500}>
                                PayPal
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Pay with your PayPal account
                              </Typography>
                            </Box>
                            {paymentData.payment_frequencies.recurring && (
                              <Chip size="small" label="Supports Recurring" color="primary" variant="outlined" />
                            )}
                          </Box>
                        }
                        sx={{ py: 1 }}
                      />
                    )}

                    {paymentData.payment_methods.coinbase && (
                      <FormControlLabel
                        value="coinbase"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                            <CurrencyBitcoinIcon sx={{ color: '#f7931a' }} />
                            <Box>
                              <Typography variant="subtitle1" fontWeight={500}>
                                Pay with Crypto
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {paymentData.crypto_token || 'USDC'}, ETH, BTC, SOL via Coinbase
                              </Typography>
                            </Box>
                            <Chip size="small" label="Low Fees" sx={{ bgcolor: alpha('#f7931a', 0.1), color: '#f7931a', fontWeight: 600 }} />
                          </Box>
                        }
                        sx={{ py: 1 }}
                      />
                    )}
                  </RadioGroup>
                </FormControl>

                {/* Enhanced Stripe Card Element */}
                {formState.selectedPaymentMethod === 'stripe' && (
                  <Fade in timeout={300}>
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Card Details
                      </Typography>
                      <Box sx={{ 
                        p: 2, 
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        borderRadius: 2,
                        background: alpha(theme.palette.background.paper, 0.8),
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          background: theme.palette.background.paper,
                        }
                      }}>
                        <CardElement
                          options={{
                            style: {
                              base: {
                                fontSize: '16px',
                                color: theme.palette.text.primary,
                                fontFamily: theme.typography.fontFamily,
                                '::placeholder': {
                                  color: theme.palette.text.disabled,
                                },
                              },
                            },
                          }}
                        />
                      </Box>
                      
                      <SecurityBadge sx={{ mt: 2 }}>
                        <LockIcon sx={{ fontSize: 14 }} />
                        256-bit SSL encryption
                      </SecurityBadge>
                    </Box>
                  </Fade>
                )}

                {/* Enhanced PayPal Buttons */}
                {formState.selectedPaymentMethod === 'paypal' && (
                <Fade in timeout={300}>
                    <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                        PayPal Payment
                    </Typography>
                    
                    {/* Debug info */}
                    {process.env.NODE_ENV === 'development' && (
                        <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                        Debug: PayPal method selected, Amount: ${formState.customAmount || 'Not set'}, 
                        Type: {formState.selectedRecurringType}, 
                        Interval: {formState.selectedRecurringInterval}
                        </Alert>
                    )}
                    
                    <Box sx={{ 
                        p: 2,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        borderRadius: 2,
                        background: alpha(theme.palette.background.paper, 0.5)
                    }}>
                        {!formState.isProcessing ? (
                        <PayPalScriptProvider 
                            options={getDynamicPayPalOptions()}
                            key={`${formState.selectedRecurringType}-${formState.selectedRecurringInterval}`}
                        >
                            <PayPalButtons
                            // Conditionally pass ONLY the appropriate handler
                            {...(formState.selectedRecurringType === 'recurring' ? {
                                // For recurring payments, use createSubscription ONLY
                                createSubscription: async () => {
                                try {
                                    console.log('PayPal createSubscription called for recurring payment');
                                    console.log('Creating subscription with interval:', formState.selectedRecurringInterval);
                                    
                                    const subscriptionId = await createPayPalOrder();
                                    console.log('PayPal subscription ID created:', subscriptionId);
                                    return subscriptionId;
                                } catch (error) {
                                    console.error('PayPal createSubscription error:', error);
                                    setFormState(prev => ({ 
                                    ...prev, 
                                    error: error.message || 'Failed to create PayPal subscription',
                                    isProcessing: false 
                                    }));
                                    throw error;
                                }
                                }
                            } : {
                                // For one-time payments, use createOrder ONLY
                                createOrder: async () => {
                                try {
                                    console.log('PayPal createOrder called for one-time payment');
                                    const orderId = await createPayPalOrder();
                                    console.log('PayPal order ID created:', orderId);
                                    return orderId;
                                } catch (error) {
                                    console.error('PayPal createOrder error:', error);
                                    setFormState(prev => ({ 
                                    ...prev, 
                                    error: error.message || 'Failed to create PayPal order',
                                    isProcessing: false 
                                    }));
                                    throw error;
                                }
                                }
                            })}
                            
                            onApprove={async (data, actions) => {
                                try {
                                console.log('PayPal onApprove called with data:', data);
                                console.log('Payment type:', formState.selectedRecurringType);
                                
                                // Set processing state
                                setFormState(prev => ({ ...prev, isProcessing: true, currentStep: 1 }));
                                
                                // For subscriptions, data will have subscriptionID
                                // For one-time payments, data will have orderID
                                if (formState.selectedRecurringType === 'recurring') {
                                    console.log('Processing subscription approval, subscriptionID:', data.subscriptionID);
                                } else {
                                    console.log('Processing order approval, orderID:', data.orderID);
                                }
                                
                                const response = await axiosInstance.post(`/verify-paypal-payment`, {
                                    orderID: data.orderID,
                                    subscriptionID: data.subscriptionID,
                                    payment_token: paymentData.token
                                });

                                if (response.data.success) {
                                    setFormState(prev => ({ 
                                    ...prev, 
                                    showThankYou: true, 
                                    isProcessing: false,
                                    currentStep: 2,
                                    paymentResult: { 
                                        method: 'paypal', 
                                        recurring: formState.selectedRecurringType === 'recurring',
                                        subscriptionID: data.subscriptionID,
                                        orderID: data.orderID
                                    }
                                    }));
                                } else {
                                    throw new Error(response.data.message || 'PayPal payment verification failed');
                                }
                                } catch (error) {
                                console.error('PayPal approval error:', error);
                                setFormState(prev => ({
                                    ...prev,
                                    error: error.response?.data?.message || error.message || 'PayPal payment failed',
                                    isProcessing: false,
                                    currentStep: 0
                                }));
                                }
                            }}
                            
                            onError={(err) => {
                                console.error('PayPal error:', err);
                                setFormState(prev => ({
                                ...prev,
                                error: `PayPal payment failed: ${err.message || 'An error occurred'}`,
                                isProcessing: false,
                                currentStep: 0
                                }));
                            }}
                            
                            onCancel={() => {
                                console.log('PayPal payment cancelled');
                                setFormState(prev => ({ 
                                ...prev, 
                                error: 'PayPal payment was cancelled',
                                isProcessing: false,
                                currentStep: 0
                                }));
                            }}
                            
                            style={{
                                layout: 'vertical',
                                color: 'blue',
                                shape: 'rect',
                                label: formState.selectedRecurringType === 'recurring' ? 'subscribe' : 'pay',
                                height: 45
                            }}
                            
                            // Only use fundingSource for one-time payments
                            {...(formState.selectedRecurringType !== 'recurring' && {
                                fundingSource: 'paypal'
                            })}
                            
                            disabled={formState.isProcessing}
                            />
                        </PayPalScriptProvider>
                        ) : (
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            height: '45px',
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            borderRadius: 1
                        }}>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            <Typography variant="body2">Processing PayPal payment...</Typography>
                        </Box>
                        )}
                    </Box>
                    
                    <SecurityBadge sx={{ mt: 2 }}>
                        <VerifiedUserIcon sx={{ fontSize: 14 }} />
                        PayPal Buyer Protection
                    </SecurityBadge>
                    </Box>
                </Fade>
                )}

                {/* Coinbase Crypto Payment */}
                {formState.selectedPaymentMethod === 'coinbase' && (
                  <Fade in timeout={300}>
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                        Crypto Payment via Coinbase
                      </Typography>

                      <Box sx={{
                        p: 3,
                        border: `1px solid ${alpha('#f7931a', 0.2)}`,
                        borderRadius: 2,
                        background: alpha('#f7931a', 0.03)
                      }}>
                        {paymentData.crypto_token && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Preferred Token:
                            </Typography>
                            <Chip
                              label={paymentData.crypto_token}
                              size="small"
                              sx={{ bgcolor: alpha('#f7931a', 0.1), color: '#f7931a', fontWeight: 600 }}
                            />
                          </Box>
                        )}

                        {paymentData.accepted_tokens && paymentData.accepted_tokens.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Accepted tokens:
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {paymentData.accepted_tokens.map(token => (
                                <Chip key={token} label={token} size="small" variant="outlined" />
                              ))}
                            </Stack>
                          </Box>
                        )}

                        <Alert severity="info" sx={{ mb: 2, borderRadius: 1 }}>
                          <Typography variant="body2">
                            You'll be redirected to Coinbase Commerce to complete your crypto payment securely.
                            Blockchain confirmations may take a few minutes.
                          </Typography>
                        </Alert>

                        <Button
                          variant="contained"
                          fullWidth
                          size="large"
                          disabled={formState.isProcessing || !parseFloat(formState.customAmount)}
                          onClick={async () => {
                            if (!validateForm()) return;
                            setFormState(prev => ({ ...prev, isProcessing: true, currentStep: 1, error: '' }));
                            try {
                              const res = await axiosInstance.post('/process-advanced-payment', {
                                payment_token: paymentData.token,
                                amount: formState.customAmount,
                                payment_method: 'coinbase',
                                customer_info: formState.customerInfo,
                                selected_purpose: formState.selectedPurpose,
                                include_processing_fee: formState.includeProcessingFee,
                                ...(embed.isEmbed && { embed: true }),
                                ...(embed.accentColor && { accent: embed.accentColor.replace('#', '') }),
                              });
                              if (res.data.success && res.data.coinbase_hosted_url) {
                                // Redirect in the same tab so Coinbase can redirect back to our success page
                                embed.embedRedirect(res.data.coinbase_hosted_url);
                              } else {
                                setFormState(prev => ({
                                  ...prev,
                                  isProcessing: false,
                                  error: res.data.message || 'Failed to initiate crypto payment.',
                                }));
                              }
                            } catch (err) {
                              setFormState(prev => ({
                                ...prev,
                                isProcessing: false,
                                error: err.response?.data?.message || 'Failed to initiate crypto payment.',
                              }));
                            }
                          }}
                          sx={{
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #f7931a, #e8850f)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #e8850f, #f7931a)',
                              transform: 'translateY(-1px)',
                            }
                          }}
                          endIcon={<OpenInNewIcon />}
                        >
                          {formState.isProcessing ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={20} color="inherit" />
                              Redirecting to Coinbase...
                            </Box>
                          ) : (
                            `Pay with Crypto — $${amounts.totalAmount}`
                          )}
                        </Button>
                      </Box>

                      <SecurityBadge sx={{ mt: 2 }}>
                        <CurrencyBitcoinIcon sx={{ fontSize: 14 }} />
                        Powered by Coinbase Commerce
                      </SecurityBadge>
                    </Box>
                  </Fade>
                )}
              </CardContent>
            </ProfessionalCard>

            {/* Enhanced Payment Summary */}
            <ProfessionalCard sx={{ mb: 3, background: alpha(theme.palette.primary.main, 0.02) }}>
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <ReceiptIcon />
                  </Avatar>
                }
                title={
                  <Typography variant="h6" fontWeight={600}>
                    Payment Summary
                  </Typography>
                }
                subheader="Review your payment details"
              />
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1">
                      Amount{isRecurring ? ` (${formState.selectedRecurringInterval})` : ''}:
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      ${amounts.baseAmount}
                    </Typography>
                  </Box>
                  
                  {(formState.includeProcessingFee || paymentData.processing_fee_handling === 'added') && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Processing Fee:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ${amounts.processingFee}
                      </Typography>
                    </Box>
                  )}
                  
                  <Divider />
                  
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    p: 2,
                    borderRadius: 2,
                    background: alpha(theme.palette.primary.main, 0.08)
                  }}>
                    <Typography variant="h6" fontWeight={700}>
                      Total{isRecurring ? ` (${formState.selectedRecurringInterval})` : ''}:
                    </Typography>
                    <Typography 
                      variant="h5" 
                      fontWeight={700}
                      sx={{ 
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        backgroundClip: 'text',
                        textFillColor: 'transparent'
                      }}
                    >
                      ${amounts.totalAmount}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </ProfessionalCard>

            {/* Disclaimer */}
            {paymentData.disclaimer && (
              <Alert 
                severity="info" 
                icon={<InfoIcon />}
                sx={{ 
                  mb: 3, 
                  borderRadius: 2,
                  background: alpha(theme.palette.info.main, 0.05)
                }}
              >
                {paymentData.disclaimer}
              </Alert>
            )}

            {/* Error Display */}
            {formState.error && (
              <Grow in timeout={300}>
                <Alert 
                  severity="error" 
                  icon={<ErrorIcon />}
                  sx={{ 
                    mb: 3, 
                    borderRadius: 2,
                    background: alpha(theme.palette.error.main, 0.05)
                  }}
                  action={
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={() => setFormState(prev => ({ ...prev, error: '' }))}
                    >
                      ✕
                    </IconButton>
                  }
                >
                  {formState.error}
                </Alert>
              </Grow>
            )}

            {/* Enhanced Submit Button - Only show for Stripe */}
            {formState.selectedPaymentMethod === 'stripe' && (
              <Box sx={{ position: 'relative' }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={formState.isProcessing || !stripe}
                  sx={{ 
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    boxShadow: theme.shadows[4],
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      transform: 'translateY(-1px)',
                      boxShadow: theme.shadows[8],
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.action.disabled, 0.12),
                      color: theme.palette.action.disabled,
                    }
                  }}
                >
                  {formState.isProcessing ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={24} color="inherit" />
                      Processing Payment...
                    </Box>
                  ) : (
                    <>
                      {paymentData.payment_type === 'donation' ? 'Donate' : 'Pay'} ${amounts.totalAmount}
                      {isRecurring && ` ${formState.selectedRecurringInterval}`}
                    </>
                  )}
                </Button>
              </Box>
            )}

            {/* Recurring Payment Info */}
            {isRecurring && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Alert 
                  severity="info" 
                  icon={<NotificationsIcon />}
                  sx={{ 
                    background: alpha(theme.palette.warning.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
                  }}
                >
                  <Typography variant="body2">
                    <strong>Recurring Payment Notice:</strong> This will set up a {formState.selectedRecurringInterval} recurring payment. 
                    You can cancel anytime from your account or by contacting support.
                  </Typography>
                </Alert>
              </Box>
            )}

            {/* Trust Indicators */}
            <Box sx={{ 
              mt: 4, 
              pt: 3, 
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              textAlign: 'center'
            }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
                <SecurityBadge>
                  <SecurityIcon sx={{ fontSize: 16 }} />
                  SSL Secured
                </SecurityBadge>
                <SecurityBadge>
                  <VerifiedUserIcon sx={{ fontSize: 16 }} />
                  PCI Compliant
                </SecurityBadge>
                <SecurityBadge>
                  <LockIcon sx={{ fontSize: 16 }} />
                  Bank-Level Security
                </SecurityBadge>
              </Stack>
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Your payment information is encrypted and secure. We never store your card details.
              </Typography>
            </Box>
          </form>
        </Box>
      </GradientPaper>
    </Container>
  );
};

export default PublicPaymentPage;